import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useNavigate, useParams } from 'react-router';
import JobCard from 'components/JobCard/Index';
import Navbar from 'components/Navbar';
import Footer from 'components/Footer/Index';
import JobFilters from 'components/feature/jobFilters';
import { JobService } from 'services/jobs/job';
import { IJob, IJobFilters } from 'core/interface/job';
import NoData from 'components/shared/NoData/Index';
import { JobCardActionEnum } from 'core/enum/jobCardAction';
import useLocales from 'hooks/useLocales';
import Image from 'components/image/Image';
import Pagination from '@material-ui/core/Pagination';

import './Index.scss';

const FilterJobs: FC = (): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filter, setFilter] = useState<IJobFilters>();
  const { jobs } = useSelector((state: RootState) => state.jobs);
  const { id } = useParams();
  const targetRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);

  useEffect(() => {
    getJobs(limit, offset);
  }, [filter, limit, offset]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const getJobs = async (limit?: number, offset?: number): Promise<void> => {
    try {
      JobService.getJobs({
        limit,
        offset,
        industries: filter?.industry ? Number(filter.industry) : undefined,
        skills: filter?.computer_skill?.length
          ? filter.computer_skill.map(Number)
          : undefined,
        degrees: filter?.degree_type ? Number(filter.degree_type) : undefined,
        locations: filter?.location?.id
          ? Number(filter.location.id)
          : undefined,
        // salaries: filter?.salary?.length
        //   ? filter.salary.map(Number)
        //   : undefined,
        compensations: filter?.compensations?.length
          ? filter.compensations.map(Number)
          : undefined,
        work_exps: filter?.career_level?.length
          ? filter.career_level.map(Number)
          : undefined,
        languages: filter?.languages?.length
          ? filter.languages.map((item) => Number(item.id))
          : undefined,
        company: id ? +id : undefined,
        is_remote:
          filter?.is_remote !== undefined ? filter.is_remote : undefined,
        contract: filter?.contract?.length
          ? filter.contract.map(Number)
          : undefined,
        company_sizes: filter?.company_sizes?.length
          ? filter.company_sizes.map(Number)
          : undefined
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleFilterOpen = (): void => {
    setShowFilter((prev) => !prev);
  };

  const handleChangeScroll = (): void => {
    if (targetRef?.current) {
      targetRef.current.scrollIntoView({
        block: 'start'
      });
    }
  };

  const handleActionComplete = async (): Promise<void> => {
    await getJobs(limit, offset);
  };

  useEffect(() => {
    handleChangeScroll();
  }, [targetRef]);

  return (
    <div
      className="filterJobs"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      <Navbar />
      <div className="fjobs" ref={targetRef}>
        <div className="top">
          {!id && (
            <div
              className="back_buttons"
              onClick={() => navigate('/dashboard')}
            >
              <Image name="arrow-left.svg" />
              {t('Back to profile')}
            </div>
          )}
          {/** this was intended for smaller screens but styling and logic is way off */}
          {/* <div className="back_buttons" onClick={handleFilterOpen}>
            <Image name="filtering.svg" />
            {t('Open Filter')}
          </div> */}
        </div>
        <div className="filter-and-jobs">
          <div className={`filtering ${showFilter ? 'show' : ''}`}>
            <JobFilters applyFilter={setFilter} />
          </div>
          <div className="fjobs__matches">
            <div className="fjobs__matches__cards">
              {jobs?.results?.length === 0 ? (
                <NoData />
              ) : (
                jobs?.results?.map((item: IJob, index: number) => (
                  <div key={index} className="fjobs__matches__card">
                    <JobCard
                      id={item.id}
                      title={item.title}
                      sub_title={item.description}
                      icon={
                        item.company.logo ??
                        '/static/images/recrout-logo-letter.jpg'
                      }
                      actions={[
                        JobCardActionEnum.MORE_INFO,
                        JobCardActionEnum.APPLY,
                        JobCardActionEnum.PROFILE_VIEW
                      ]}
                      matches={[
                        { percent: +item.score, title: 'Talent Match' },
                        { percent: +item.culture_score, title: 'Cultural Fit' }
                      ]}
                      isApplied={!!item.is_applied}
                      assessmentRequired={item.assessment_required}
                      onActionComplete={handleActionComplete}
                      competencies={item.competencies}
                      competencyMinScores={item?.competency_min_scores}
                      cultureCompetencies={
                        item.culture_profile?.competencies || []
                      }
                      cultureProfile={item.culture_profile}
                    />
                  </div>
                ))
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16
                }}
              >
                <Pagination
                  className="pagination"
                  count={Math.ceil((jobs?.count || 0) / limit)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="main" style={{ flex: 1 }}>
        {/* ... main content ... */}
      </div>
      <Footer />
    </div>
  );
};

export default FilterJobs;
